import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────
   GOOGLE DRIVE IMAGE URL
   drive.google.com/thumbnail?id=X&sz=wN is the most reliable
   public endpoint — works for any file shared as
   "Anyone with the link can view". No auth, no CORS issues.
   sz=w600  → grid thumbnails
   sz=w1600 → lightbox full view
───────────────────────────────────────────────────────────── */
const gImg = (id, size) =>
  `https://drive.google.com/thumbnail?id=${id}&sz=w${size || 1600}`;

const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@300;400;500;600&display=swap');
@keyframes lbIn   { from{opacity:0} to{opacity:1} }
@keyframes imgIn  { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }
@keyframes spin   { to{transform:rotate(360deg)} }
`;

export default function ProjectDetails() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project,  setProject]  = useState(null);
  const [images,   setImages]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [hovered,  setHovered]  = useState(null);

  const API  = process.env.REACT_APP_API_URL || 'https://localhost:7069';
  const GKEY = process.env.REACT_APP_GOOGLE_API_KEY;

  useEffect(() => {
    (async () => {
      if (!projectId) return;
      try {
        const r = await fetch(`${API}/api/projects/${projectId}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        setProject(d);
        if (d.googleDriveFolderId && GKEY) await loadImages(d.googleDriveFolderId);
        else setLoading(false);
      } catch (e) { setError(e.message); setLoading(false); }
    })();
  }, [projectId]);

  const loadImages = async (folderId) => {
    try {
      const q = encodeURIComponent(
        `'${folderId}' in parents and trashed=false and mimeType contains 'image/'`
      );
      const r = await fetch(
        `https://www.googleapis.com/drive/v3/files?q=${q}&key=${GKEY}&fields=files(id,name)&pageSize=100`
      );
      if (!r.ok) throw new Error(`Drive API ${r.status}`);
      const d = await r.json();
      setImages((d.files || []).map(f => ({
        id:        f.id,
        name:      f.name,
        thumbnail: gImg(f.id, 600),   // grid thumbnails
        url:       gImg(f.id),        // full-res lightbox (no size = original)
      })));
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const nav = useCallback((e) => {
    if (selected === null) return;
    if (e.key === 'ArrowRight') setSelected(p => (p+1) % images.length);
    if (e.key === 'ArrowLeft')  setSelected(p => (p-1+images.length) % images.length);
    if (e.key === 'Escape')     setSelected(null);
  }, [selected, images.length]);
  useEffect(() => { window.addEventListener('keydown', nav); return () => window.removeEventListener('keydown', nav); }, [nav]);

  const total = images.length;

  if (loading) return (
    <div style={S.page}><style>{css}</style>
      <div style={S.center}>
        <div style={S.spinner}/><p style={S.loadTxt}>Loading gallery…</p>
      </div>
    </div>
  );

  if (error || !project) return (
    <div style={S.page}><style>{css}</style>
      <div style={S.center}>
        <div style={{fontSize:'2.5rem',marginBottom:'1rem'}}>🙏</div>
        <p style={{...S.loadTxt, color:'#e57373'}}>{error||'Project not found'}</p>
        <button style={S.backBtn} onClick={() => navigate('/projects')}>← Back to Projects</button>
      </div>
    </div>
  );

  return (
    <>
      <style>{css}</style>
      <div style={S.page}>

        {/* ── HEADER ── */}
        <div style={S.heroWrap}>
          <div style={S.heroBg}/>
          <div style={S.heroInner}>
            <button style={S.backBtn}
              onMouseEnter={e=>Object.assign(e.currentTarget.style,{background:'rgba(255,255,255,0.2)'})}
              onMouseLeave={e=>Object.assign(e.currentTarget.style,{background:'rgba(255,255,255,0.1)'})}
              onClick={()=>navigate('/projects')}
            >← Back to Projects</button>

            <span style={S.statusBadge(project.status)}>
              <span style={{width:7,height:7,borderRadius:'50%',background:project.status==='ongoing'?'#34d399':'#94a3b8',display:'inline-block'}}/>
              {project.status.charAt(0).toUpperCase()+project.status.slice(1)}
            </span>
            <h1 style={S.heroTitle}>{project.projectName}</h1>
            <div style={S.heroMeta}>
              <span style={S.chip}>📅 {project.year}</span>
              {total>0&&<span style={S.chip}>📸 {total} photo{total!==1?'s':''}</span>}
            </div>
            {project.description&&<p style={S.heroDesc}>{project.description}</p>}
          </div>
        </div>

        {/* ── GALLERY ── */}
        <div style={S.galleryWrap}>
          <div style={S.galleryRule}>
            <div style={S.ruleLine}/><span style={S.ruleLabel}>Photo Gallery</span><div style={S.ruleLine}/>
          </div>

          {total===0 ? (
            <div style={S.empty}>
              <div style={{fontSize:'3rem',marginBottom:'1rem'}}>📷</div>
              <p style={{color:'#aaa',fontSize:'0.9rem'}}>No photographs yet for this project</p>
            </div>
          ) : (
            <div style={S.grid}>
              {images.map((img,idx)=>(
                <div key={img.id}
                  style={{...S.thumb,
                    transform: hovered===idx?'scale(1.02)':'scale(1)',
                    boxShadow: hovered===idx?'0 16px 40px rgba(0,0,0,0.18)':'0 2px 8px rgba(0,0,0,0.07)',
                  }}
                  onClick={()=>setSelected(idx)}
                  onMouseEnter={()=>setHovered(idx)}
                  onMouseLeave={()=>setHovered(null)}
                >
                  <img src={img.thumbnail} alt={img.name} loading="lazy"
                    style={{...S.thumbImg, transform:hovered===idx?'scale(1.08)':'scale(1)'}}
                    onError={e=>{ if(!e.target.dataset.fb){e.target.dataset.fb='1'; e.target.src=gImg(img.id,400);} }}
                  />
                  <div style={{...S.thumbOverlay, opacity:hovered===idx?1:0}}>
                    <span style={S.thumbViewLabel}>⊕ View Full</span>
                    <span style={S.thumbCounter}>{idx+1} / {total}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════ LIGHTBOX ══════════ */}
      {selected!==null&&(
        <div style={S.lb} onClick={()=>setSelected(null)}>
          <button style={S.lbClose}
            onClick={e=>{e.stopPropagation();setSelected(null);}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.15)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(0,0,0,0.6)'}
          >✕</button>

          {total>1&&<>
            <button style={{...S.lbArrow,left:'1rem'}}
              onClick={e=>{e.stopPropagation();setSelected(p=>(p-1+total)%total);}}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(243,156,18,0.3)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(0,0,0,0.6)'}
            >‹</button>
            <button style={{...S.lbArrow,right:'1rem'}}
              onClick={e=>{e.stopPropagation();setSelected(p=>(p+1)%total);}}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(243,156,18,0.3)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(0,0,0,0.6)'}
            >›</button>
          </>}

          <div style={S.lbImgWrap} onClick={e=>e.stopPropagation()}>
            <img key={selected} src={images[selected].url} alt={images[selected].name}
              style={S.lbImg}
              onError={e=>{if(!e.target.dataset.fb){e.target.dataset.fb='1';e.target.src=`https://drive.google.com/thumbnail?id=${images[selected].id}&sz=w800`;}}}
            />
          </div>

          <div style={S.lbBar} onClick={e=>e.stopPropagation()}>
            <span style={S.lbCount}>{selected+1} / {total}</span>
            <span style={S.lbName}>{images[selected].name.replace(/\.[^/.]+$/,'')}</span>
            <div style={S.lbDots}>
              {images.slice(Math.max(0,selected-4),selected+5).map((_,i)=>{
                const a=Math.max(0,selected-4)+i;
                return <span key={a} onClick={()=>setSelected(a)} style={{
                  width:a===selected?'20px':'6px', height:'6px', borderRadius:'3px',
                  background:a===selected?'#f39c12':'rgba(255,255,255,0.3)',
                  cursor:'pointer', transition:'all 0.25s', display:'inline-block',
                }}/>;
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const S = {
  page:    { minHeight:'100vh', background:'#f5f4f0', fontFamily:"'DM Sans', sans-serif" },
  center:  { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:'1rem' },
  spinner: { width:'36px', height:'36px', border:'3px solid rgba(33,128,141,0.2)', borderTopColor:'#21808d', borderRadius:'50%', animation:'spin 0.8s linear infinite' },
  loadTxt: { fontFamily:"'DM Sans',sans-serif", fontSize:'0.82rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#aaa' },

  /* back btn */
  backBtn: {
    display:'inline-flex', alignItems:'center', gap:'0.4rem',
    padding:'0.45rem 1rem', background:'rgba(255,255,255,0.1)',
    border:'1px solid rgba(255,255,255,0.25)', borderRadius:'999px',
    color:'#fff', fontSize:'0.78rem', letterSpacing:'0.05em',
    cursor:'pointer', transition:'background 0.2s', marginBottom:'1.25rem',
  },

  /* hero */
  heroWrap:  { position:'relative', overflow:'hidden', padding:'3rem 3rem 2.5rem' },
  heroBg:    { position:'absolute', inset:0, background:'linear-gradient(135deg, #0b3d4a 0%, #134d5e 55%, #1a6474 100%)' },
  heroInner: { position:'relative', zIndex:1, maxWidth:'1100px', margin:'0 auto' },
  statusBadge: s=>({
    display:'inline-flex', alignItems:'center', gap:'6px',
    padding:'4px 12px', borderRadius:'999px', marginBottom:'0.75rem',
    fontSize:'0.7rem', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase',
    background: s==='ongoing'?'rgba(52,211,153,0.15)':'rgba(148,163,184,0.15)',
    color: s==='ongoing'?'#34d399':'#94a3b8',
    border:`1px solid ${s==='ongoing'?'rgba(52,211,153,0.3)':'rgba(148,163,184,0.2)'}`,
  }),
  heroTitle: { fontFamily:"'Playfair Display',Georgia,serif", fontSize:'clamp(1.8rem,4vw,2.8rem)', fontWeight:700, color:'#fff', margin:'0 0 0.75rem', lineHeight:1.15 },
  heroMeta:  { display:'flex', gap:'0.6rem', flexWrap:'wrap', marginBottom:'0.75rem' },
  chip:      { background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', color:'rgba(255,255,255,0.75)', padding:'4px 12px', borderRadius:'999px', fontSize:'0.78rem' },
  heroDesc:  { fontSize:'0.96rem', color:'rgba(255,255,255,0.62)', lineHeight:1.7, margin:0, fontStyle:'italic', maxWidth:'65ch' },

  /* gallery */
  galleryWrap:  { padding:'2.5rem 3rem 4rem', maxWidth:'1400px', margin:'0 auto' },
  galleryRule:  { display:'flex', alignItems:'center', gap:'1rem', marginBottom:'2rem' },
  ruleLine:     { flex:1, height:'1px', background:'rgba(0,0,0,0.09)' },
  ruleLabel:    { fontFamily:"'DM Sans',sans-serif", fontSize:'0.68rem', fontWeight:600, letterSpacing:'0.18em', textTransform:'uppercase', color:'#21808d', whiteSpace:'nowrap' },

  /* grid */
  grid:  { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'10px' },
  thumb: { position:'relative', aspectRatio:'4/3', overflow:'hidden', cursor:'pointer', borderRadius:'10px', background:'#ddd', transition:'transform 0.3s ease, box-shadow 0.3s ease' },
  thumbImg: { width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94)' },
  thumbOverlay: {
    position:'absolute', inset:0, transition:'opacity 0.3s',
    background:'linear-gradient(to top, rgba(11,61,74,0.88) 0%, rgba(11,61,74,0.15) 55%, transparent 100%)',
    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', padding:'0.85rem',
  },
  thumbViewLabel: { fontFamily:"'DM Sans',sans-serif", color:'#fff', fontSize:'0.78rem', fontWeight:600, letterSpacing:'0.08em', marginBottom:'0.25rem' },
  thumbCounter:   { fontFamily:"'DM Sans',sans-serif", color:'rgba(255,255,255,0.5)', fontSize:'0.68rem' },
  empty: { textAlign:'center', padding:'5rem 2rem' },

  /* lightbox */
  lb:       { position:'fixed', inset:0, zIndex:9999, background:'rgba(3,3,3,0.97)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', display:'flex', alignItems:'center', justifyContent:'center', animation:'lbIn 0.2s ease' },
  lbClose:  { position:'fixed', top:'1.25rem', right:'1.5rem', zIndex:10001, width:'42px', height:'42px', borderRadius:'50%', border:'1px solid rgba(255,255,255,0.2)', background:'rgba(0,0,0,0.6)', color:'#fff', fontSize:'1rem', cursor:'pointer', transition:'background 0.2s', display:'flex', alignItems:'center', justifyContent:'center' },
  lbArrow:  { position:'fixed', top:'50%', transform:'translateY(-50%)', zIndex:10001, width:'54px', height:'54px', borderRadius:'50%', border:'1px solid rgba(255,255,255,0.15)', background:'rgba(0,0,0,0.6)', color:'#fff', fontSize:'1.8rem', cursor:'pointer', transition:'background 0.2s', display:'flex', alignItems:'center', justifyContent:'center' },
  lbImgWrap:{ maxWidth:'90vw', maxHeight:'82vh', display:'flex', alignItems:'center', justifyContent:'center' },
  lbImg:    { maxWidth:'88vw', maxHeight:'80vh', objectFit:'contain', borderRadius:'4px', boxShadow:'0 40px 100px rgba(0,0,0,0.85)', animation:'imgIn 0.28s cubic-bezier(0.25,0.46,0.45,0.94)', display:'block' },
  lbBar:    { position:'fixed', bottom:0, left:0, right:0, zIndex:10001, padding:'1.25rem 2rem', background:'linear-gradient(to top, rgba(0,0,0,0.92) 0%, transparent 100%)', display:'flex', alignItems:'center', justifyContent:'center', gap:'1.25rem' },
  lbCount:  { fontFamily:"'DM Sans',sans-serif", fontSize:'0.8rem', letterSpacing:'0.12em', color:'#f39c12', fontWeight:600 },
  lbName:   { fontFamily:"'DM Sans',sans-serif", fontSize:'0.8rem', color:'rgba(255,255,255,0.5)', maxWidth:'40ch', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  lbDots:   { display:'flex', gap:'5px', alignItems:'center' },
};